import { SystemConnectionType } from '@Components';
import {
	DateFormatOptions,
	DateType,
	FLAGS,
	QuoteFormatOptions,
	QuoteType,
} from '@Types';
import { ConnectionFormField } from '@Utils/connectionStringReplacer';
import { SystemConnectionFormHelpers as SCFHelpers } from '@Utils/systemConnectionFormHelpers';
import { useGlobalization, useIsFeatureEnabled } from '@innovyze/stylovyze';

type SystemConnectionDriverReturn = {
	determineValuesFromServerType: (
		name: ConnectionFormField,
		newValue: string | null,
	) => Record<string, string>;
	systemConnectionTypes: () => SystemConnectionType[];
	buildOpcuaUrl: (
		serverAddress: string,
		port: string,
		resourcePath: string,
	) => string;
};

export const useSystemConnectionDriver = (): SystemConnectionDriverReturn => {
	const { t } = useGlobalization();
	const isOpcuaEnabled = useIsFeatureEnabled(FLAGS.INFO_360_ADMIN_OPCUA);
	const isOledbEnabled = useIsFeatureEnabled(FLAGS.INFO_360_ADMIN_OLEDB);

	const buildOpcuaUrl = (
		serverAddress: string,
		port: string,
		resourcePath: string,
	) => {
		return SCFHelpers.buildOpcuaUrl(serverAddress, port, resourcePath);
	};

	const systemConnectionTypes = (): SystemConnectionType[] => {
		const translations: Record<'ODBC' | 'OLEDB' | 'OPC-UA', string> = {
			ODBC: t('ODBC'),
			OLEDB: t('OLEDB'),
			'OPC-UA': t('OPC-UA'),
		};
		const response = SCFHelpers.buildSystemConnectionTypes({
			isOpcuaEnabled,
			isOledbEnabled,
			translations,
		});
		return response;
	};

	const determineValuesFromServerType = (
		name: ConnectionFormField,
		newValue: string | null,
	) => {
		const port = SCFHelpers.getPort(newValue);
		const dateOption = SCFHelpers.getDateOption(newValue);
		const quoteOption = SCFHelpers.getQuoteOption(newValue);
		const tagFormat = SCFHelpers.getTagFormat(newValue);
		const timestampConversion = SCFHelpers.getTsConversion(newValue);
		const queryPrefix = SCFHelpers.getQueryPrefix(newValue);
		const queryGroupSize = SCFHelpers.getQueryGroupSize(newValue);

		const newFormData = {
			[name]: newValue || '',
			dateTimeFormat: newValue
				? DateFormatOptions[dateOption as DateType].value
				: DateFormatOptions.ANSI.value,
			quoteFormat: newValue
				? QuoteFormatOptions[quoteOption as QuoteType].value
				: QuoteFormatOptions.DOUBLE_QUOTE.value,
			port: port,
			timestampConversion: timestampConversion,
			tagFormat: tagFormat,
			queryPrefix: queryPrefix,
			queryGroupSize: queryGroupSize,
		};

		return newFormData;
	};

	return {
		determineValuesFromServerType,
		systemConnectionTypes,
		buildOpcuaUrl,
	};
};
